import { motion } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, TrendingUp, Building2 } from "lucide-react";
import { CONTACT, STATS } from "@/lib/site";
import { CountUp } from "./Reveal";
import { scrollTo } from "./Navbar";

const HEADLINE = ["Business Loans &", "Working Capital", "Up to ₹5 Crore"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section id="hero" data-testid="hero" className="relative overflow-hidden bg-surface pt-[72px]">
      {/* Kinetic blobs */}
      <div className="blob w-[520px] h-[520px] bg-brand/25 -top-40 -left-32" />
      <div className="blob w-[460px] h-[460px] bg-emerald/25 top-40 -right-24" style={{ animationDelay: "-6s" }} />
      <div className="blob w-[380px] h-[380px] bg-brand/10 bottom-0 left-1/3" style={{ animationDelay: "-12s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 px-4 py-1.5 mb-7 tick-shadow"
          >
            <span className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
            <span className="text-xs font-semibold tracking-wide text-navy">Trusted MSME Loan Advisory · Delhi NCR</span>
          </motion.div>

          <h1 className="font-display font-black text-navy tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-xl text-base md:text-lg text-ink/60 leading-relaxed"
          >
            Helping MSMEs unlock growth through faster, transparent and hassle-free business financing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <button
              data-testid="hero-book-consultation"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-navy transition-colors"
            >
              Book Free Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              data-testid="hero-whatsapp"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-navy font-semibold border border-black/5 hover:border-emerald hover:text-emerald transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <a
              data-testid="hero-call"
              href={`tel:${CONTACT.phoneRaw}`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-navy font-semibold border border-black/5 hover:border-brand hover:text-brand transition-colors"
            >
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </motion.div>

          <div className="mt-11 grid grid-cols-3 gap-6 max-w-md">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 + i * 0.1, duration: 0.5 }}
              >
                <div className="font-display font-extrabold text-3xl text-navy">
                  <CountUp end={s.value} suffix={s.suffix} />
                </div>
                <div className="text-xs text-ink/50 mt-1 leading-snug">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[28px] overflow-hidden tick-shadow border border-white"
          >
            <img
              src="https://images.unsplash.com/photo-1577415124269-fc1140a69e91?w=900&q=80"
              alt="MSME business owners reviewing loan financing options"
              className="w-full h-[440px] lg:h-[520px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
          </motion.div>

          {/* Floating glass cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="absolute -left-4 lg:-left-10 top-10 bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-white tick-shadow"
            data-testid="hero-card-partners"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand/10 text-brand"><Building2 className="w-5 h-5" /></span>
              <div>
                <div className="font-display font-bold text-navy leading-none">Multiple</div>
                <div className="text-xs text-ink/50 mt-1">Banking Partners</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="absolute -right-3 lg:-right-8 bottom-10 bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-white tick-shadow"
            data-testid="hero-card-growth"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald/10 text-emerald"><TrendingUp className="w-5 h-5" /></span>
              <div>
                <div className="font-display font-bold text-navy leading-none">₹5 Cr+</div>
                <div className="text-xs text-ink/50 mt-1">Funding Capacity</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
